/**
 * Utility class for Service Worker registration and update management.
 * 
 * This class handles the registration of the service worker for Progressive Web App (PWA)
 * functionality, including automatic update detection and notification. It provides methods
 * to register the service worker, check for updates, and notify the application when new
 * versions are available.
 * 
 * The service worker enables:
 * - Offline functionality through caching
 * - Automatic app updates when new builds are deployed
 * - Background update checking
 * - Seamless version updates without manual page refresh
 */
export class ServiceWorkerUtil {
    /** The service worker registration instance, null if not registered */
    private static registration: ServiceWorkerRegistration | null = null;
    
    /** Flag indicating if an update is available and waiting to be activated */
    private static updateAvailable = false;
    
    /** Array of callbacks to be invoked when an update becomes available */
    private static updateCallbacks: Array<() => void> = [];

    /**
     * Register the service worker for PWA functionality.
     *
     * This method registers the service worker script located at '/sw.js' and sets up
     * event listeners for update detection. It will:
     * - Check if service workers are supported in the current browser
     * - Register the service worker with scope '/'
     * - Immediately check for any available updates
     * - Set up listeners for update events and controller changes
     *
     * When a new service worker is detected, it will notify all registered callbacks.
     * When a new service worker takes control, the page will optionally reload based on the autoReloadOnUpdate parameter.
     *
     * @param {boolean} autoReloadOnUpdate - If true, automatically reload the page when a new service worker takes control. Default: false.
     * @returns {Promise<void>} A promise that resolves when registration is complete
     * @throws {Error} If service worker registration fails (errors are logged, not thrown)
     *
     * @example
     * ```typescript
     * // Register without auto-reload (user controls update)
     * ServiceWorkerUtil.register().then(() => {
     *   console.log('Service worker registered');
     * });
     *
     * // Register with auto-reload enabled
     * ServiceWorkerUtil.register(true).then(() => {
     *   console.log('Service worker registered with auto-reload');
     * });
     * ```
     */
    public static async register(autoReloadOnUpdate: boolean = false): Promise<void> {
        if (!('serviceWorker' in navigator)) {
            console.log('[Service Worker] Not supported in this browser');
            return;
        }

        try {
            const registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/',
            });

            this.registration = registration;

            console.log('[Service Worker] Registered successfully:', registration.scope);

            // Check for updates immediately
            await this.checkForUpdates();

            // Listen for updates
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (!newWorker) {
                    console.log('[Service Worker] No new service worker found');
                    return;
                }

                console.log('[Service Worker] New service worker found');

                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New service worker is installed and waiting
                        console.log('[Service Worker] New version available');
                        this.updateAvailable = true;
                        this.notifyUpdateCallbacks();
                    }
                });
            });

            // Listen for controller change (when new service worker takes control)
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                console.log('[Service Worker] New service worker activated');
                // Reload the page only if auto-reload is enabled
                if (autoReloadOnUpdate) {
                    window.location.reload();
                }
            });

        } catch (error) {
            console.error('[Service Worker] Registration failed:', error);
        }
    }

    /**
     * Check for service worker updates.
     * 
     * This method manually triggers an update check by calling the service worker
     * registration's update() method. The browser will compare the current service
     * worker script with the one on the server, and if a new version is found, it
     * will be downloaded and installed in the background.
     * 
     * This is called automatically:
     * - On initial service worker registration
     * - Periodically via startPeriodicUpdateCheck()
     * - When explicitly invoked by the application
     * 
     * @returns {Promise<void>} A promise that resolves when the update check completes
     * 
     * @example
     * ```typescript
     * // Manually check for updates
     * await ServiceWorkerUtil.checkForUpdates();
     * ```
     */
    public static async checkForUpdates(): Promise<void> {
        if (!this.registration) {
            return;
        }

        try {
            await this.registration.update();
            console.log('[Service Worker] Update check completed');
        } catch (error) {
            console.error('[Service Worker] Update check failed:', error);
        }
    }

    /**
     * Skip waiting and activate the new service worker immediately.
     * 
     * When a new service worker is installed, it enters a "waiting" state until all
     * pages using the old service worker are closed. This method sends a message to
     * the waiting service worker to skip this waiting period and activate immediately.
     * 
     * After calling this method, the page will automatically reload (via the
     * 'controllerchange' event listener) to use the new service worker.
     * 
     * This should be called when the user is ready to apply an update, typically
     * after showing a notification that an update is available.
     * 
     * @returns {Promise<void>} A promise that resolves immediately (message is sent asynchronously)
     * 
     * @example
     * ```typescript
     * if (ServiceWorkerUtil.isUpdateAvailable()) {
     *   // Show notification to user
     *   // Then activate the update
     *   await ServiceWorkerUtil.skipWaiting();
     * }
     * ```
     */
    public static async skipWaiting(): Promise<void> {
        if (!this.registration || !this.registration.waiting) {
            return;
        }

        // Send message to waiting service worker to skip waiting
        this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    /**
     * Check if a service worker update is available and waiting to be activated.
     * 
     * @returns {boolean} True if an update is available, false otherwise
     * 
     * @example
     * ```typescript
     * if (ServiceWorkerUtil.isUpdateAvailable()) {
     *   console.log('New version available!');
     * }
     * ```
     */
    public static isUpdateAvailable(): boolean {
        return this.updateAvailable;
    }

    /**
     * Register a callback to be notified when a service worker update becomes available.
     * 
     * When a new service worker is installed and ready to activate, all registered
     * callbacks will be invoked. This allows the application to show notifications,
     * update UI, or take other actions when an update is detected.
     * 
     * Multiple callbacks can be registered, and all will be called when an update
     * becomes available.
     * 
     * @param {() => void} callback - The function to call when an update is available
     * @returns {() => void} A function to unregister this callback
     * 
     * @example
     * ```typescript
     * const unregister = ServiceWorkerUtil.onUpdateAvailable(() => {
     *   showUpdateNotification();
     * });
     * // Later, to unregister:
     * unregister();
     * ```
     */
    public static onUpdateAvailable(callback: () => void): () => void {
        this.updateCallbacks.push(callback);
        
        // Return an unregister function
        return () => {
            const index = this.updateCallbacks.indexOf(callback);
            if (index > -1) {
                this.updateCallbacks.splice(index, 1);
            }
        };
    }

    /**
     * Notify all registered callbacks that a service worker update is available.
     * 
     * This private method is called internally when a new service worker is detected
     * and installed. It iterates through all registered callbacks and invokes them.
     * 
     * @private
     */
    private static notifyUpdateCallbacks(): void {
        this.updateCallbacks.forEach(callback => callback());
    }

    /**
     * Start periodically checking for service worker updates.
     * 
     * This method sets up an interval to check for updates every 5 minutes. This ensures
     * that users will receive updates even if they keep the app open for extended periods.
     * 
     * The interval continues running for the lifetime of the application. It's recommended
     * to call this once after service worker registration, typically in the application's
     * initialization code.
     * 
     * @returns {void}
     * 
     * @example
     * ```typescript
     * ServiceWorkerUtil.register().then(() => {
     *   ServiceWorkerUtil.startPeriodicUpdateCheck();
     * });
     * ```
     */
    public static startPeriodicUpdateCheck(): void {
        // Check for updates every 5 minutes (300,000 milliseconds)
        setInterval(() => {
            this.checkForUpdates();
        }, 5 * 60 * 1000);
    }
}

